import { Tag, Typography } from "antd";
import styles from "./LadleCard.module.less";
import { HeatIcon } from "~/components/heatIcon/HeatIcon";
import { useCallback, useContext } from "react";
import { CopilotMessageContext } from "~/providers/CopilotMessageProvider";
import { useClearCache } from "~/hooks/useSwrManagement";
import {
  GET_CAMPAIGN_HEATS_FOR_ASSET,
  GET_FILE_PATH,
  GET_LIDAR_DATA_VISUALIZATION_SUMMARIES_FOR_LAST_SPECIFIC_HEATS,
  GET_PROCESSED_THERMAL_IMAGES,
  GET_REQUESTS,
  GET_REQUESTS_FOR_HEAT,
} from "~/constants/swrKey";
import { CopilotLogsType } from "~/pages/copilotMessage/types/copilotLogs";
import { useUserLogContext } from "~/providers/UserLogProvider";
import { isConversationGeneric } from "~/pages/copilotMessage/components/conversationDetail/components/conversationMessages/components/mikeMessage/helpers/mikeMessageParametersHelper";
import { useLanguage } from "~/hooks/useLanguage";

enum ServiceList {
  proccessingHeat = "Processing Heat",
  outOfService = "Out of Service",
  available = "Available",
}

interface LadleCardProps {
  id: string;
  laddleAssetName: string;
  currentHeatNumber: number;
  serviceState: string;
  destination?: string;
  location: string;
}

function LadleCard({
  id,
  laddleAssetName,
  currentHeatNumber,
  serviceState,
  destination,
  location,
}: LadleCardProps) {
  const { selectedConversation, conversationList, setSelectedConversation } =
    useContext(CopilotMessageContext);

  const { clearCache } = useClearCache();

  const { insertUserLog } = useUserLogContext();

  const { formatMessage } = useLanguage();

  // Check if this is a generic ladle
  const isGeneric = isConversationGeneric(id);

  // Helper function to get localized service state
  const getLocalizedServiceState = (state: string) => {
    switch (state) {
      case ServiceList.proccessingHeat:
        return formatMessage("ServiceState.ProcessingHeat");
      case ServiceList.outOfService:
        return formatMessage("ServiceState.OutOfService");
      case ServiceList.available:
        return formatMessage("ServiceState.Available");
      default:
        return state;
    }
  };

  const onSelectLaddleCard = useCallback(() => {
    clearCache([
      GET_FILE_PATH,
      GET_LIDAR_DATA_VISUALIZATION_SUMMARIES_FOR_LAST_SPECIFIC_HEATS,
      GET_PROCESSED_THERMAL_IMAGES,
      GET_REQUESTS,
      GET_REQUESTS_FOR_HEAT,
      GET_CAMPAIGN_HEATS_FOR_ASSET,
    ]);
    insertUserLog(CopilotLogsType.LadleClicked);
    setSelectedConversation(conversationList?.find((x) => x.id == id));
  }, [
    setSelectedConversation,
    id,
    conversationList,
    insertUserLog,
    clearCache,
  ]);

  return (
    <div
      className={`${styles.LadleCard} ${
        selectedConversation?.id == id ? styles.selected : ""
      } ${isGeneric ? styles.generic : ""}`}
      onClick={onSelectLaddleCard}
    >
      {isGeneric ? (
        <>
          {/* Top content wrapper for generic ladle */}
          <div className={styles.topContent}>
            <div className={styles.ladleHeatWrapper}>
              <Typography.Title level={2} className={styles.ladleNumber}>
                Ladle {formatMessage("LadleCard.Generic.Unassigned")}
              </Typography.Title>
            </div>
          </div>

          {/* Full width message at bottom */}
          <div className={styles.genericMessage}>
            <Typography.Text
              style={{
                fontStyle: "italic",
                color: "#8c8c8c",
                fontSize: "14px",
                lineHeight: "1.4",
              }}
            >
              {formatMessage("LadleCard.Generic.AnalysesMessage")}
            </Typography.Text>
          </div>
        </>
      ) : (
        <>
          {/* Normal ladle layout */}
          <div className={styles.ladleHeatWrapper}>
            <Typography.Title level={2} className={styles.ladleNumber}>
              Ladle {laddleAssetName}
            </Typography.Title>

            <div className={styles.heatAndIconWrapper}>
              <HeatIcon
                width="24"
                height="24"
                color={
                  serviceState === ServiceList.outOfService ? "gray" : "red"
                }
              />

              <div className={styles.heatWrapper}>
                <Typography.Title level={3} className={styles.currentHeat}>
                  {currentHeatNumber}
                </Typography.Title>
                {/* TODO: predicted heat values will be integrated here if they come from the service
                <Typography.Text className={styles.predictedHeat}>
                  (87-92)
                </Typography.Text> */}
              </div>
            </div>
          </div>

          <div className={styles.serviceLocationWrapper}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Tag
                style={{ margin: 0 }}
                color={
                  serviceState === ServiceList.proccessingHeat
                    ? "#34D37C"
                    : serviceState === ServiceList.available
                      ? "#69b1ff"
                      : "#A0A0A0"
                }
              >
                <Typography.Title
                  level={3}
                  style={{ color: "#fff", margin: 0 }}
                >
                  {getLocalizedServiceState(serviceState)}
                </Typography.Title>
              </Tag>
            </div>
            <div className={styles.destinationText}>
              <Typography.Title
                level={4}
                style={{
                  margin: 0,
                  fontWeight: 400,
                  color: "#636e72",
                }}
              >
                {location}
                {destination ? " ➜ " + destination : ""}
              </Typography.Title>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default LadleCard;
